/* global AbortController, CustomEvent */
import { Controller } from '@hotwired/stimulus'
import mapboxgl from 'mapbox-gl'
import { categoryParam } from '../utils/mobility_category_filter'

const STATION_COLORS = {
  available: '#1f9d55',
  low: '#d97706',
  empty_or_full: '#dc2626',
  offline: '#9ca3af'
}
const GREY = '#9ca3af'
const VEHICLE_COLOR = '#6b7280'
const ZONE_COLOR = '#0f766e'
const HUB_COLOR = '#24A718'
const HUB_MARKER_MIN_ZOOM = 9
const HUB_RING_MIN_ZOOM = 12
const HUB_CLUSTER_MAX_ZOOM = 11
const HUB_CLUSTER_RADIUS = 50
const ZONE_MIN_ZOOM = 14
const STATION_CLUSTER_MAX_ZOOM = 13
const VEHICLE_CLUSTER_MAX_ZOOM = 14
const LOAD_DELAY = 200
const MAP_WAIT_INTERVAL = 25
const MAP_WAIT_MAX_ATTEMPTS = 400
// The layer is fetched for the viewport plus this share of its span on every side, so small
// pans and zoom-ins are answered from what is already drawn instead of a new request.
const COVERAGE_PADDING = 0.2
// GBFS feeds refresh about every 30s, so older data is refetched even when it covers the view.
const COVERAGE_TTL = 30000

export default class extends Controller {
  static targets = [
    'toggle', 'filters', 'chip', 'operator', 'legend', 'drawer',
    'name', 'meta', 'status', 'bikes', 'docks', 'capacity',
    'operatorName', 'feed', 'asOf', 'detail', 'staleNote'
  ]

  // overlayCategories: categories the overlay draws itself, whose location pins are hidden.
  // categories: every category the Mobility Layer lists; the category pills switch them off one by one.
  // prescoped: the page was opened as the Mobility Layer type, so the server scoped the first pins request.
  static values = { url: String, overlayCategories: Array, categories: Array, prescoped: Boolean }

  connect () {
    this.enabled = false
    this.stationChip = true
    this.vehicleChip = true
    this.hiddenCategories = new Set()
    this.map = null
    this.sourcesReady = false
    this.zones = []
    this.zonesLoaded = false
    this.stationsById = new Map()
    this.hubsById = new Map()
    this.requestSerial = 0
    this.mapAttempts = 0
    this.coverage = null
    this.pendingPayload = null
    this.hubs = []
    this.ringsShown = false
    // Opened as the Mobility Layer type: the server already scopes the map gem's first
    // pins/cards request (see LocationsController#apply_mobility_layer_page_scope), so it
    // must not be fetched a second time.
    this.pinsScoped = this.prescopedValue
    this.onMove = () => {
      this.hideLocationPins()
      if (this.enabled) {
        this.syncHubRings()
        this.scheduleLoad(false)
      }
    }
    this.onMapClickHandler = (event) => this.onMapClick(event)
    this.onSearch = () => this.applyMobilityLocationScope()
    window.addEventListener('updateSearch', this.onSearch, true)
    this.onPageShow = () => this.syncEnabledWithToggle()
    window.addEventListener('pageshow', this.onPageShow)
    this.waitForMap()
    this.syncEnabledWithToggle()
  }

  disconnect () {
    window.removeEventListener('updateSearch', this.onSearch, true)
    window.removeEventListener('pageshow', this.onPageShow)
    clearTimeout(this.loadTimer)
    clearTimeout(this.mapTimer)
    if (this.map) {
      this.map.off('moveend', this.onMove)
      this.map.off('click', this.onMapClickHandler)
    }
    if (this.abortController) this.abortController.abort()
    if (this.popup) this.popup.remove()
  }

  // The map belongs to the gem's `map` controller, which only exposes it as an instance
  // field (the pinned gem revision emits no ready/moveend events and sets no
  // element.locationMap), so read it from the controller itself and listen on the Mapbox
  // map directly. The gem ships its own bundled Stimulus application (location_management-
  // compiled.js), separate from this.application, and publishes it as window.Stimulus, so
  // `map` is only registered there.
  resolveMap () {
    if (this.element.locationMap) return this.element.locationMap
    const applications = [window.Stimulus, this.application].filter(Boolean)
    for (const application of applications) {
      const controller = application.getControllerForElementAndIdentifier(this.element, 'map')
      if (controller && controller.map) return controller.map
    }
    return null
  }

  waitForMap () {
    clearTimeout(this.mapTimer)
    if (this.map) return
    // Opened as the Mobility Layer type: the map may not exist yet when this controller
    // connects, so nothing could be scoped. Scope the map's first load once it does.
    if (this.captureMap(this.resolveMap())) {
      if (this.enabled) this.refreshLocations()
      return
    }
    this.mapAttempts += 1
    if (this.mapAttempts >= MAP_WAIT_MAX_ATTEMPTS) {
      console.warn('[mobility-layer] map controller exposed no map; the Mobility overlay will not be drawn')
      return
    }
    this.mapTimer = setTimeout(() => this.waitForMap(), MAP_WAIT_INTERVAL)
  }

  syncEnabledWithToggle () {
    if (!this.hasToggleTarget) return
    if (this.toggleTarget.checked === this.enabled) return
    this.toggle()
  }

  toggle () {
    this.captureMap(this.resolveMap())
    this.enabled = this.hasToggleTarget && this.toggleTarget.checked
    if (this.enabled) {
      this.stationChip = true
      this.vehicleChip = true
      this.hiddenCategories.clear()
      this.applyCategoryFilter()
      if (this.hasOperatorTarget) this.operatorTarget.value = 'all'
      this.syncChips()
      this.filtersTarget.classList.remove('hidden')
      this.legendTarget.classList.remove('hidden')
      this.zonesLoaded = false
      this.coverage = null
      this.scheduleLoad(true)
      this.refreshLocations()
    } else {
      this.filtersTarget.classList.add('hidden')
      this.legendTarget.classList.add('hidden')
      this.closeDrawer()
      this.clearOverlay()
      this.hiddenCategories.clear()
      if (this.abortController) this.abortController.abort()
      clearTimeout(this.loadTimer)
      this.setLoading(false)
      this.coverage = null
      this.pendingPayload = null
      this.pinsScoped = false
      // An explicit "false": the server scopes a request from the page URL when the param is absent
      window.searchParams = window.searchParams || {}
      delete window.searchParams.category
      window.searchParams.mobility_layer = 'false'
      window.dispatchEvent(new CustomEvent('updateSearch'))
    }
  }

  applyMobilityLocationScope () {
    if (!this.enabled) return
    window.searchParams = window.searchParams || {}
    window.searchParams.mobility_layer = 'true'
  }

  refreshLocations () {
    this.applyMobilityLocationScope()
    // Pins and cards already came back scoped from the server
    if (this.pinsScoped) return
    // No map yet: onReady scopes the first load once the map announces itself
    if (!this.map) return
    this.whenPinsSourceReady(() => window.dispatchEvent(new CustomEvent('updateSearch')))
  }

  // The map controller loads its first page of locations and pins unscoped and only
  // then creates the 'mapData' pins source. Refetching before that would discard that
  // first load and leave the source missing, so wait for it.
  whenPinsSourceReady (callback) {
    const map = this.map
    if (map.getSource('mapData')) {
      callback()
      return
    }
    const onSourceData = (event) => {
      if (event.sourceId !== 'mapData' || !map.getSource('mapData')) return
      map.off('sourcedata', onSourceData)
      setTimeout(callback, 0)
    }
    map.on('sourcedata', onSourceData)
  }

  // A pill can switch the live overlay (data-kind), a category of our own sites in the list and
  // pins (data-category), or both: the station pill hides the live stations and the station cards.
  toggleKind (event) {
    const { kind, category } = event.currentTarget.dataset
    if (kind === 'stations') this.stationChip = !this.stationChip
    if (kind === 'vehicles') this.vehicleChip = !this.vehicleChip
    if (category) this.setCategoryHidden(category, kind === 'stations' ? !this.stationChip : !this.hiddenCategories.has(category))
    this.syncChips()
    this.applyVisibility()
    if (!this.stationChip) this.closeDrawer()
    if (category) {
      this.applyCategoryFilter()
      // The server-scoped first load no longer matches the pills, so the list and pins must be refetched
      this.pinsScoped = false
      this.refreshLocations()
    }
  }

  setCategoryHidden (category, hidden) {
    if (hidden) this.hiddenCategories.add(category)
    else this.hiddenCategories.delete(category)
  }

  // The filter goes through the same request as the list and the pins (rather than hiding
  // pins on the client, which would leave them counted in the clusters).
  applyCategoryFilter () {
    window.searchParams = window.searchParams || {}
    const param = categoryParam(this.categoriesValue, this.hiddenCategories)
    if (param) window.searchParams.category = param
    else delete window.searchParams.category
  }

  changeOperator () {
    this.closeDrawer()
    this.coverage = null
    this.scheduleLoad(false)
    this.drawZones()
  }

  closeDrawer () {
    if (this.hasDrawerTarget) {
      this.drawerTarget.classList.add('hidden')
      this.drawerTarget.classList.remove('is-stale')
    }
  }

  // Returns true only when this call attached a map that was not captured before.
  captureMap (map) {
    if (!map || this.map === map) return false
    this.map = map
    this.map.on('click', this.onMapClickHandler)
    this.map.on('moveend', this.onMove)
    if (this.enabled) this.scheduleLoad(true)
    this.hideLocationPins()
    return true
  }

  // immediate: first load / toggle, nothing to debounce. Map moves wait for the pan to settle.
  scheduleLoad (immediate = false) {
    clearTimeout(this.loadTimer)
    this.loadTimer = setTimeout(() => this.load(), immediate ? 0 : LOAD_DELAY)
  }

  async load () {
    if (!this.enabled || !this.map || !this.urlValue) return
    const viewport = this.bounds()
    if (!viewport) return

    // Zones are only drawn from ZONE_MIN_ZOOM, so they are fetched once the map gets that close
    const needsZones = !this.zonesLoaded && this.map.getZoom() >= ZONE_MIN_ZOOM
    const operator = this.operatorKey()
    if (!needsZones && this.covers(viewport, operator)) return

    const bounds = this.paddedBounds(viewport)
    const kinds = ['stations', 'vehicles', 'hubs']
    if (needsZones) kinds.push('zones')
    const query = new URLSearchParams({ ...bounds, kinds: kinds.join(','), operator })

    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()
    this.requestSerial += 1
    const serial = this.requestSerial
    this.setLoading(true)

    try {
      const response = await fetch(`${this.urlValue}?${query.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal
      })
      if (!response.ok) return
      const payload = await response.json()
      if (serial !== this.requestSerial || !this.enabled) return
      // A truncated answer does not hold everything in its box, so it cannot answer later views
      this.coverage = payload.truncated ? null : { bounds, operator, at: Date.now() }
      this.applyPayload(payload)
    } catch (error) {
      if (error.name === 'AbortError') return
      throw error
    } finally {
      if (serial === this.requestSerial) this.setLoading(false)
    }
  }

  applyPayload (payload) {
    this.syncOperators(payload.operators || [])
    if (payload.zones) {
      this.zones = payload.zones
      this.zonesLoaded = true
    }
    // The style is still loading: keep the answer and draw it when it is ready, no refetch
    if (!this.ensureSources()) {
      this.pendingPayload = payload
      return
    }
    this.pendingPayload = null
    this.drawStations(payload.stations || [])
    this.drawVehicles(payload.vehicles || [])
    this.drawHubs(payload.hubs || [])
    this.drawZones()
    this.applyVisibility()
  }

  covers (viewport, operator) {
    const coverage = this.coverage
    if (!coverage || coverage.operator !== operator) return false
    if (Date.now() - coverage.at > COVERAGE_TTL) return false
    const box = coverage.bounds
    return viewport.latitude_1 <= box.latitude_1 &&
      viewport.latitude_2 >= box.latitude_2 &&
      viewport.longitude_1 <= box.longitude_1 &&
      viewport.longitude_2 >= box.longitude_2
  }

  paddedBounds (viewport) {
    const latitudePad = (viewport.latitude_1 - viewport.latitude_2) * COVERAGE_PADDING
    const longitudePad = (viewport.longitude_1 - viewport.longitude_2) * COVERAGE_PADDING
    return {
      latitude_1: viewport.latitude_1 + latitudePad,
      latitude_2: viewport.latitude_2 - latitudePad,
      longitude_1: viewport.longitude_1 + longitudePad,
      longitude_2: viewport.longitude_2 - longitudePad
    }
  }

  setLoading (loading) {
    if (this.hasFiltersTarget) this.filtersTarget.classList.toggle('is-loading', loading)
  }

  bounds () {
    const box = this.map.getBounds()
    return {
      latitude_1: box.getNorth(),
      longitude_1: box.getEast(),
      latitude_2: box.getSouth(),
      longitude_2: box.getWest()
    }
  }

  operatorKey () {
    if (!this.hasOperatorTarget) return 'all'
    return this.operatorTarget.value || 'all'
  }

  syncOperators (operators) {
    if (!this.hasOperatorTarget) return
    const current = this.operatorTarget.value || 'all'
    this.operatorTarget.replaceChildren()
    const all = document.createElement('option')
    all.value = 'all'
    all.textContent = 'All'
    this.operatorTarget.appendChild(all)
    operators.forEach((operator) => {
      const option = document.createElement('option')
      option.value = operator.key
      option.textContent = operator.name
      this.operatorTarget.appendChild(option)
    })
    const known = Array.from(this.operatorTarget.options).some((option) => option.value === current)
    this.operatorTarget.value = known ? current : 'all'
  }

  syncChips () {
    this.chipTargets.forEach((chip) => {
      const { kind, category } = chip.dataset
      let active
      if (kind === 'stations') active = this.stationChip
      else if (kind === 'vehicles') active = this.vehicleChip
      else active = !this.hiddenCategories.has(category)
      chip.classList.toggle('is-active', active)
      chip.setAttribute('aria-pressed', active ? 'true' : 'false')
    })
  }

  ensureSources () {
    if (this.sourcesReady) return true
    if (!this.map) return false
    if (!this.map.isStyleLoaded()) {
      if (!this.waitingForStyle) {
        this.waitingForStyle = true
        const retry = () => {
          this.waitingForStyle = false
          if (!this.enabled) return
          if (this.pendingPayload) {
            this.applyPayload(this.pendingPayload)
          } else {
            this.load()
          }
        }
        this.map.once('load', retry)
        this.map.once('idle', retry)
      }
      return false
    }
    const empty = { type: 'FeatureCollection', features: [] }

    this.map.addSource('mobility-zones', { type: 'geojson', data: empty })
    this.map.addSource('mobility-hubs', { type: 'geojson', data: empty })
    this.map.addSource('mobility-hub-centers', {
      type: 'geojson',
      data: empty,
      cluster: true,
      clusterMaxZoom: HUB_CLUSTER_MAX_ZOOM,
      clusterRadius: HUB_CLUSTER_RADIUS
    })
    this.map.addSource('mobility-vehicles', {
      type: 'geojson',
      data: empty,
      cluster: true,
      clusterMaxZoom: VEHICLE_CLUSTER_MAX_ZOOM,
      clusterRadius: 70
    })
    this.map.addSource('mobility-stations', {
      type: 'geojson',
      data: empty,
      cluster: true,
      clusterMaxZoom: STATION_CLUSTER_MAX_ZOOM,
      clusterRadius: 64
    })

    this.map.addLayer({
      id: 'mobility-zones',
      type: 'line',
      source: 'mobility-zones',
      minzoom: ZONE_MIN_ZOOM,
      paint: {
        'line-color': ['case', ['==', ['get', 'stale'], 1], GREY, ZONE_COLOR],
        'line-width': 2,
        'line-dasharray': [2, 2],
        'line-opacity': ['case', ['==', ['get', 'stale'], 1], 0.4, 0.9]
      }
    })
    addDashedHubIcon(this.map)
    this.map.addLayer({
      id: 'mobility-hub-clusters',
      type: 'symbol',
      source: 'mobility-hub-centers',
      minzoom: HUB_MARKER_MIN_ZOOM,
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': 'mobility-hub-dashed',
        'icon-size': ['step', ['get', 'point_count'], 0.5, 10, 0.65, 30, 0.85],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      }
    })
    this.map.addLayer({
      id: 'mobility-hub-cluster-count',
      type: 'symbol',
      source: 'mobility-hub-centers',
      minzoom: HUB_MARKER_MIN_ZOOM,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: {
        'text-color': HUB_COLOR,
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5
      }
    })
    this.map.addLayer({
      id: 'mobility-hub-rings',
      type: 'line',
      source: 'mobility-hubs',
      minzoom: HUB_RING_MIN_ZOOM,
      paint: {
        'line-color': HUB_COLOR,
        'line-width': 2,
        'line-dasharray': [2, 2]
      }
    })
    this.map.addLayer({
      id: 'mobility-hub-labels',
      type: 'symbol',
      source: 'mobility-hub-centers',
      filter: ['!', ['has', 'point_count']],
      minzoom: HUB_RING_MIN_ZOOM,
      layout: {
        'text-field': ['concat', 'Hub: ', ['get', 'name']],
        'text-size': 12,
        'text-offset': [0, -1.2],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: { 'text-color': HUB_COLOR }
    })
    this.map.addLayer({
      id: 'mobility-vehicle-clusters',
      type: 'circle',
      source: 'mobility-vehicles',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': VEHICLE_COLOR,
        'circle-radius': ['step', ['get', 'point_count'], 14, 20, 18, 100, 24],
        'circle-opacity': 0.9
      }
    })
    this.map.addLayer({
      id: 'mobility-vehicle-cluster-count',
      type: 'symbol',
      source: 'mobility-vehicles',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: { 'text-color': '#ffffff' }
    })
    this.map.addLayer({
      id: 'mobility-vehicles',
      type: 'circle',
      source: 'mobility-vehicles',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 5,
        'circle-color': ['case', ['==', ['get', 'stale'], 1], GREY, VEHICLE_COLOR],
        'circle-opacity': ['case', ['==', ['get', 'stale'], 1], 0.4, 0.95],
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff'
      }
    })
    this.map.addLayer({
      id: 'mobility-station-clusters',
      type: 'circle',
      source: 'mobility-stations',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': STATION_COLORS.available,
        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 30, 26],
        'circle-opacity': 0.9,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    })
    this.map.addLayer({
      id: 'mobility-station-cluster-count',
      type: 'symbol',
      source: 'mobility-stations',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: { 'text-color': '#ffffff' }
    })
    this.map.addLayer({
      id: 'mobility-stations',
      type: 'circle',
      source: 'mobility-stations',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 14,
        'circle-color': stationColor(),
        'circle-opacity': ['case', ['==', ['get', 'stale'], 1], 0.45, 0.95],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    })
    this.map.addLayer({
      id: 'mobility-station-badges',
      type: 'symbol',
      source: 'mobility-stations',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'text-field': ['case',
          ['==', ['typeof', ['get', 'bikes_available']], 'number'],
          ['to-string', ['get', 'bikes_available']],
          ''
        ],
        'text-size': 11,
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true
      },
      paint: { 'text-color': '#ffffff' }
    })

    this.bindPointer('mobility-stations')
    this.bindPointer('mobility-station-badges')
    this.bindPointer('mobility-station-clusters')
    this.bindPointer('mobility-vehicles')
    this.bindPointer('mobility-vehicle-clusters')
    this.bindPointer('mobility-zones')
    this.bindPointer('mobility-hub-clusters')
    this.bindPointer('mobility-hub-rings')
    this.bindPointer('mobility-hub-labels')

    this.map.on('click', 'mobility-stations', (event) => this.openStation(event))
    this.map.on('click', 'mobility-station-badges', (event) => this.openStation(event))
    this.map.on('click', 'mobility-vehicles', (event) => this.openVehicle(event))
    this.map.on('click', 'mobility-zones', (event) => this.openZone(event))
    this.map.on('click', 'mobility-hub-clusters', (event) => this.expandCluster('mobility-hub-centers', event))
    this.map.on('click', 'mobility-hub-rings', (event) => this.openHub(event))
    this.map.on('click', 'mobility-hub-labels', (event) => this.openHub(event))
    this.map.on('click', 'mobility-vehicle-clusters', (event) => this.expandCluster('mobility-vehicles', event))
    this.map.on('click', 'mobility-station-clusters', (event) => this.expandCluster('mobility-stations', event))

    this.sourcesReady = true
    return true
  }

  bindPointer (layerId) {
    this.map.on('mouseenter', layerId, () => { this.map.getCanvas().style.cursor = 'pointer' })
    this.map.on('mouseleave', layerId, () => { this.map.getCanvas().style.cursor = '' })
  }

  drawStations (stations) {
    if (!this.ensureSources()) return
    this.stationsById = new Map(stations.map((station) => [String(station.id), station]))
    this.map.getSource('mobility-stations').setData({
      type: 'FeatureCollection',
      features: stations.map((station) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [station.longitude, station.latitude] },
        properties: {
          id: station.id,
          availability: station.availability,
          bikes_available: station.bikes_available,
          stale: station.stale ? 1 : 0
        }
      }))
    })
  }

  drawVehicles (vehicles) {
    if (!this.ensureSources()) return
    this.map.getSource('mobility-vehicles').setData({
      type: 'FeatureCollection',
      features: vehicles.map((vehicle) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [vehicle.longitude, vehicle.latitude] },
        properties: {
          id: vehicle.id,
          battery_label: vehicle.battery_percent == null ? '—' : `${vehicle.battery_percent}%`,
          operator_name: vehicle.operator_name || '',
          stale: vehicle.stale ? 1 : 0
        }
      }))
    })
  }

  drawHubs (hubs) {
    if (!this.ensureSources()) return
    this.hubs = hubs
    this.hubsById = new Map(hubs.map((hub) => [String(hub.id), hub]))
    this.map.getSource('mobility-hub-centers').setData({
      type: 'FeatureCollection',
      features: hubs.map((hub) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [hub.longitude, hub.latitude] },
        properties: { id: hub.id, name: hub.name || 'Hub' }
      }))
    })
    this.syncHubRings(true)
  }

  // The rings are only drawn from HUB_RING_MIN_ZOOM, so their polygons are only built then
  syncHubRings (force = false) {
    if (!this.sourcesReady || !this.map) return
    const show = this.map.getZoom() >= HUB_RING_MIN_ZOOM
    if (!force && show === this.ringsShown) return
    this.ringsShown = show
    const rings = show ? this.hubs : []
    this.map.getSource('mobility-hubs').setData({
      type: 'FeatureCollection',
      features: rings.map((hub) => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [circlePolygon(hub.longitude, hub.latitude, hub.radius_meters || 75)]
        },
        properties: { id: hub.id, name: hub.name || 'Hub' }
      }))
    })
  }

  drawZones () {
    if (!this.zonesLoaded) return
    if (!this.ensureSources()) return
    const operator = this.operatorKey()
    const zones = this.zones.filter((zone) => operator === 'all' || zone.feed_key === operator)
    this.map.getSource('mobility-zones').setData({
      type: 'FeatureCollection',
      features: zones.map((zone) => ({
        type: 'Feature',
        geometry: zone.geometry,
        properties: { name: zone.name, stale: zone.stale ? 1 : 0 }
      }))
    })
  }

  applyVisibility () {
    if (!this.sourcesReady) return
    this.setVisibility('mobility-stations', this.stationChip)
    this.setVisibility('mobility-station-badges', this.stationChip)
    this.setVisibility('mobility-station-clusters', this.stationChip)
    this.setVisibility('mobility-station-cluster-count', this.stationChip)
    this.setVisibility('mobility-vehicles', this.vehicleChip)
    this.setVisibility('mobility-vehicle-clusters', this.vehicleChip)
    this.setVisibility('mobility-vehicle-cluster-count', this.vehicleChip)
    this.setVisibility('mobility-zones', this.vehicleChip)
  }

  setVisibility (layerId, visible) {
    if (!this.map.getLayer(layerId)) return
    this.map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none')
  }

  clearOverlay () {
    if (!this.sourcesReady) return
    const empty = { type: 'FeatureCollection', features: [] }
    this.map.getSource('mobility-stations').setData(empty)
    this.map.getSource('mobility-vehicles').setData(empty)
    this.map.getSource('mobility-zones').setData(empty)
    this.map.getSource('mobility-hubs').setData(empty)
    this.map.getSource('mobility-hub-centers').setData(empty)
    this.stationsById = new Map()
    this.hubsById = new Map()
    this.hubs = []
    this.ringsShown = false
    if (this.popup) this.popup.remove()
  }

  openStation (event) {
    const feature = event.features && event.features[0]
    if (!feature) return
    const station = this.stationsById.get(String(feature.properties.id))
    if (!station) return
    this.keepDrawer = true
    if (this.popup) this.popup.remove()
    this.drawerTarget.classList.remove('is-hub')
    this.nameTarget.textContent = station.name || 'Station'
    const meta = [station.short_name, station.external_id].filter(Boolean).join(' · ')
    this.metaTarget.textContent = station.address ? `${station.address}${meta ? ` · ${meta}` : ''}` : meta
    this.statusTarget.textContent = station.status_label || ''
    this.statusTarget.dataset.availability = station.availability || 'offline'
    this.bikesTarget.textContent = countText(station.bikes_available)
    this.docksTarget.textContent = countText(station.docks_available)
    this.capacityTarget.textContent = countText(station.capacity)
    this.operatorNameTarget.textContent = station.operator_name || ''
    this.feedTarget.textContent = station.feed_label || ''
    this.asOfTarget.textContent = station.as_of || '—'
    this.detailTarget.href = station.detail_path
    this.drawerTarget.classList.toggle('is-stale', station.stale === true)
    this.drawerTarget.classList.remove('hidden')
  }

  openHub (event) {
    const feature = event.features && event.features[0]
    if (!feature) return
    const hub = this.hubsById.get(String(feature.properties.id))
    if (!hub || !hub.detail_path) return
    this.keepDrawer = true
    if (this.popup) this.popup.remove()
    this.drawerTarget.classList.add('is-hub')
    this.drawerTarget.classList.remove('is-stale')
    this.nameTarget.textContent = hub.name || 'Hub'
    this.metaTarget.textContent = 'Mobility hub'
    this.detailTarget.href = hub.detail_path
    this.drawerTarget.classList.remove('hidden')
  }

  openVehicle (event) {
    const feature = event.features && event.features[0]
    if (!feature) return
    event.originalEvent.stopPropagation()
    const properties = feature.properties
    const operator = properties.operator_name ? `${properties.operator_name} · ` : ''
    this.showPopup(event.lngLat, `${operator}Battery ${properties.battery_label || '—'}`)
  }

  openZone (event) {
    const feature = event.features && event.features[0]
    if (!feature) return
    event.originalEvent.stopPropagation()
    this.showPopup(event.lngLat, feature.properties.name)
  }

  showPopup (lngLat, text) {
    if (this.popup) this.popup.remove()
    const node = document.createElement('div')
    node.textContent = text
    this.popup = new mapboxgl.Popup({ closeButton: true, offset: 8 })
      .setLngLat(lngLat)
      .setDOMContent(node)
      .addTo(this.map)
  }

  expandCluster (sourceId, event) {
    const feature = event.features && event.features[0]
    if (!feature) return
    event.originalEvent.stopPropagation()
    const clusterId = feature.properties.cluster_id
    this.map.getSource(sourceId).getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (error) return
      this.map.easeTo({ center: feature.geometry.coordinates, zoom })
    })
  }

  onMapClick (event) {
    if (this.keepDrawer) {
      this.keepDrawer = false
      return
    }
    if (!this.sourcesReady) return
    const hit = this.map.queryRenderedFeatures(event.point, {
      layers: ['mobility-stations', 'mobility-station-badges', 'mobility-station-clusters', 'mobility-vehicles', 'mobility-vehicle-clusters', 'mobility-zones', 'mobility-hub-clusters', 'mobility-hub-rings', 'mobility-hub-labels']
    })
    if (hit.length === 0) this.closeDrawer()
  }

  hideLocationPins () {
    if (!this.map) return
    const apply = () => {
      if (!this.map.getLayer('unclustered-point')) return false
      this.map.setFilter('unclustered-point', ['all',
        ...this.overlayCategoriesValue.map((category) => ['!=', ['get', 'category'], category])
      ])
      return true
    }
    if (!apply()) this.map.once('idle', () => apply())
  }
}

function stationColor () {
  return ['case',
    ['==', ['get', 'stale'], 1], STATION_COLORS.offline,
    ['==', ['get', 'availability'], 'available'], STATION_COLORS.available,
    ['==', ['get', 'availability'], 'low'], STATION_COLORS.low,
    ['==', ['get', 'availability'], 'empty_or_full'], STATION_COLORS.empty_or_full,
    STATION_COLORS.offline
  ]
}

function countText (value) {
  return value == null ? '—' : String(value)
}

function addDashedHubIcon (map) {
  if (map.hasImage('mobility-hub-dashed')) return
  const size = 128
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  context.strokeStyle = HUB_COLOR
  context.lineWidth = 8
  context.setLineDash([14, 10])
  context.beginPath()
  context.arc(size / 2, size / 2, size / 2 - 8, 0, Math.PI * 2)
  context.stroke()
  map.addImage('mobility-hub-dashed', context.getImageData(0, 0, size, size), { pixelRatio: 2 })
}

function circlePolygon (longitude, latitude, radiusMeters, steps = 64) {
  const earthRadius = 6371008.8
  const angular = radiusMeters / earthRadius
  const lat = latitude * Math.PI / 180
  const lng = longitude * Math.PI / 180
  const ring = []

  for (let step = 0; step <= steps; step += 1) {
    const bearing = (step / steps) * 2 * Math.PI
    const lat2 = Math.asin(
      Math.sin(lat) * Math.cos(angular) +
      Math.cos(lat) * Math.sin(angular) * Math.cos(bearing)
    )
    const lng2 = lng + Math.atan2(
      Math.sin(bearing) * Math.sin(angular) * Math.cos(lat),
      Math.cos(angular) - Math.sin(lat) * Math.sin(lat2)
    )
    ring.push([lng2 * 180 / Math.PI, lat2 * 180 / Math.PI])
  }

  return ring
}
